<template>
    <div>

        <form>

            <fieldset class="my-4">

                <legend>Select data set type</legend>

                <section class="form-row">

                    <select class="form-control border border-dark" v-model="dataFormIndex">
                        <option v-for="(item, numericIndex) of dataForms" :value="numericIndex">{{ item.name + " - " + item.period.name }}</option>
                    </select>

                </section>

            </fieldset>

            <fieldset class="my-4" v-show="isTypeSelected">

                <legend>Select category</legend>

                <section class="nav nav-pills nav-justified">
                    <a
                        class="nav-item nav-link"
                        :class="{ 'active' : (index == categoryIndex) }"
                        v-for="(item, index) of categoriesArray"
                        :key="index"
                        href="#"
                        @click="setCategoryIndex(index)" >
                        {{ item.name }}
                    </a>
                </section>

            </fieldset>

            <fieldset class="my-4" v-show="isCategorySelected">

                <legend>Select measurement</legend>

                <section class="nav nav-pills nav-justified">
                    <a
                        class="nav-item nav-link"
                        :class="{ 'active' : (index == measurementIndex) }"
                        v-for="(item, index) of measurementArray"
                        :key="index"
                        href="#"
                        @click="setMeasurementIndex(index)" >
                        {{ item.name }}
                    </a>
                </section>

            </fieldset>

        </form>

        <article class="table-responsive">

            <div class="alert alert-info text-center" v-if="loadingState">
                <span class="fa fa-cog fa-spin"></span>
                Currently retrieving your values to validate
                <span class="fa fa-cog fa-spin"></span>
            </div>

             <b-tabs nav-id="validation-tables-tabs" tabs-id="dougnat-card-tabs-content" :data-loading="loadingState">

                <b-tab label="Unchecked" id="data-tab-unverified">

                    <card-table
                        title="All Messages"
                        :items="uncheckedValues"
                        :headers="tableHeader"
                        :controls="tableControls">

                        <template slot="cell" slot-scope="cellData">
                            <div
                                class="btn btn-sm btn-primary border border-dark"
                                @click="validate(cellData.rowData.item)"
                                v-if="userPermissions.edit" >
                                Validate
                            </div>

                            <div
                                class="btn btn-sm btn-warning border border-dark"
                                @click="invalidate(cellData.rowData.item)"
                                v-if="userPermissions.edit" >
                                Reject
                            </div>
                        </template>
                    </card-table>

                </b-tab>

                <b-tab label="Verified" id="data-tab-verified">

                    <card-table
                        title="All Messages"
                        :items="validValues"
                        :headers="tableHeader"
                        :controls="tableControls">
                    </card-table>

                </b-tab>

                <b-tab label="Rejected" id="data-tab-rejected">

                    <card-table
                        title="All Messages"
                        :items="rejectedValues"
                        :headers="tableHeader"
                        :controls="tableControls">

                        <template slot="cell" slot-scope="cellData">
                            <div
                                class="btn btn-sm btn-warning border border-dark"
                                @click="invalidate(cellData.rowData.item)"
                                v-if="userPermissions.edit" >
                                Resend notification
                            </div>
                        </template>
                    </card-table>

                </b-tab>

            </b-tabs>

        </article>

    </div>
</template>

<script>

import Utils from '../mixins/ObjectUtilsMixin';

export default {

    mixins: [Utils],

    props: {

        dataForms: {
            type: Array,
            required: true,
        },

        userPermissions: {
            type: Object,
            required: true
        },

    },

    data() {
        return {
            dataFormIndex: -1,
            categoryIndex: -1,
            measurementIndex: -1,
            valuesArray: [],
            loadingState: false,
            url: window.APP_URL + "/data-collection/data-validation/validation/",

            tableControls: {
                search: true,
                sort: true,
                itemsPerPage: 10,
            },

            tableHeader: [
                { key: 'data_set.name', label: 'From data set' },
                { key: 'data_set.entity.mobileName', label: 'entity' },
                { key: 'valid_for_date', label: 'valid for date' },
                { key: 'value', label: 'value' },
                { key: 'unit', label: 'unit' },
                { key: 'note', label: 'notes' },
            ],

        };
    },

    computed: {

        dataForm() {
            return ((this.dataFormIndex >= 0) ? this.dataForms[this.dataFormIndex] : null);
        },

        isTypeSelected() {
            return (this.dataForm != null);
        },

        categoriesArray() {
            return ((this.isTypeSelected && this.dataForm.measurement_categories != null ) ? this.dataForm.measurement_categories : []);
        },

        category() {
            return (
                (this.categoryIndex >= 0 && this.categoriesArray.length > 0 && this.categoryIndex < this.categoriesArray.length) ?
                    this.categoriesArray[this.categoryIndex] : null
            );
        },

        isCategorySelected() {
            return (this.category != null);
        },

        measurementArray() {
            let tempArray = [];

            if (this.isCategorySelected && this.dataForm != null && this.dataForm.measurements != null) {
                for (let item of this.dataForm.measurements) {
                    if (item != null) {
                        if (this.category.id === item.measurement_category_id || item.measurement_category_id === null) tempArray.push(item);
                    }
                }
            }

            return tempArray;
        },

        measurement() {
            return (
                (this.measurementIndex >= 0 && this.measurementArray.length > 0 && this.measurementIndex < this.measurementArray.length) ?
                this.measurementArray[this.measurementIndex] :
                null
            );
        },

        validValues() {
            return this.valuesArray.filter((value) => { return value.valid === true; });
        },

        rejectedValues() {
            return this.valuesArray.filter((value) => { return value.valid === false; });
        },

        uncheckedValues() {
            return this.valuesArray.filter((value) => { return value.valid === null; });
        }

    },

    watch: {

        measurement() {
            if (this.measurement === null) return;

            this.loadingState = true;

            window.axios.get(this.url + this.measurement.id)
                .then((response) => {
                    this.valuesArray = [];
                    this.valuesArray.push.apply(this.valuesArray, response.data);
                    this.valuesArray.forEach((value) => { value.unit = this.measurement.unit; });
                    this.loadingState = false;
                })
                .catch((error) => { this.onError(); });
        }

    },

    methods: {

        validate(item) {
            window.axios.post(this.url + item.id)
                .then((response) => {
                    let value = response.data;
                    value.unit = this.measurement.unit; 
                    this.spliceByObjReference(this.valuesArray, item, response.data);
                })
                .catch((error) => { this.onError(error); });
        },

        invalidate(item) {
            let data = {};
            data.message = "" + window.prompt("Enter message:", "");

            window.axios.patch(this.url + item.id, data)
                .then((response) => {
                    let value = response.data;
                    value.unit = this.measurement.unit;
                    this.spliceByObjReference(this.valuesArray, item, value);
                })
                .catch((error) => { this.onError(error); });
        },

        setCategoryIndex(value) {
            this.measurementIndex = -1;
            this.valuesArray = [];
            this.categoryIndex = ((value >= 0) ? value : -1);
        },

        setMeasurementIndex(value) {
            this.measurementIndex = ((value >= 0) ? value : -1);
        },

        onError(error) {
            window.alert(error);
            this.loadingState = false;
        }

    }

};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/validation/ValidationValuesFilter.vue